import { cva } from 'class-variance-authority'

/**
 * The chip that opens a menu — "All sectors ▾", "Sectors · 3 of 11 ▾".
 *
 * Figma thickens the edge to 1.5px on Open and Filled. Kept at 1px here and
 * carried by colour alone: the system's whole elevation model is the 1px
 * hairline, and a border that changes width inside a fixed-height box shifts
 * its own label by half a pixel every time the menu opens.
 *
 * Hover and open light the edge from the toolbar's resting `--control-border`
 * to `border-border-strong` (the pages revamp, 2026-10-27, after the mock's
 * triggers and searches), so a member sees where the menu will hang before
 * committing the click, and hover changes nothing else. It is not the focus
 * ring, which stays the offset outline. A trigger holding a selection keeps the
 * accent's hairline tint, `border-border-accent`, with its summary in the link
 * colour.
 *
 * 44px below `lg`, 36px above (Figma 34, 4-grid). The trigger sits in filter
 * rows next to 36px buttons, tabs and searches, so the desktop height has to
 * match them, and a button (unlike the native select in SelectChip) can simply
 * grow without any iOS zoom trap. The label is the buttons' 14px, as Figma sets
 * it.
 */
export const selectTriggerVariants = cva(
  // sz:kit — the mock's white control ring (owner ruling A2).
  'shrink-0 cursor-pointer items-center gap-2 rounded-sm border text-label-l whitespace-nowrap transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control disabled:pointer-events-none disabled:opacity-38',
  {
    variants: {
      /**
       * `chip` is the filter pill the Market family already uses. `slot` is
       * Compare's "＋ Add broker ▾" — the dashed placeholder that sits in the
       * row of picked-broker cards and grows to their height.
       *
       * A variant rather than a second component because everything that
       * matters is shared: Popover hands both the same `aria-expanded` /
       * `aria-controls` / `aria-haspopup` triple and the same click and
       * ArrowDown handlers, and the caret flips on `open` either way. Only the
       * box differs. Forking it would be the near-duplicate this system keeps
       * being warned about.
       */
      variant: {
        chip: 'inline-flex h-11 bg-muted px-3 lg:h-9',
        slot: 'flex h-full min-h-16 w-full border-dashed bg-transparent px-4',
      },
      state: {
        resting:
          'border-control-border text-foreground hover:border-border-strong focus-visible:border-border-strong',
        open: 'border-border-strong text-foreground',
        // sz:kit — the two accent opens (`SelectTrigger openTone`). `accent` is the stock page's
        // `stc-select.is-open`: the gold edge. `line` is the Signal run picker's (`sz-select`):
        // the accent's hairline tint (the mock thickens it with a half-pixel ring; sub-pixel, so
        // it drops).
        openAccent: 'border-ring text-foreground',
        openLine: 'border-border-accent text-foreground',
        // A filled trigger states the selection in the link colour, which is
        // chrome here — the trigger is a filter control, never a data cell.
        // The fill gold is for fills only; words take `text-link`.
        filled: 'border-border-accent text-link',
      },
    },
    defaultVariants: { variant: 'chip', state: 'resting' },
  },
)
